'use client'

import { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { usePetPricing } from '@/shared/hooks/use-pet-pricing'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Textarea } from '@/shared/ui/textarea'
import { PetSize } from '@monorepo/functions/src/types/reservations'

type PetDetailsFormProps = {
    form: UseFormReturn<any>
    petIndex: number
    onRemove: () => void
    petPrice: number
    canRemove?: boolean
    singlePet?: boolean
    removePricing?: boolean
}

export function PetDetailsForm({
    form,
    petIndex,
    onRemove,
    petPrice,
    canRemove = false,
    singlePet,
    removePricing,
}: PetDetailsFormProps) {
    const { t } = useTranslation()
    const { getSizeFromWeight } = usePetPricing()

    useEffect(() => {
        const subscription = form.watch((value, { name }) => {
            const weightField = singlePet ? 'pet.weight' : `pets.${petIndex}.weight`
            if (name?.startsWith(weightField)) {
                const weight = parseFloat(singlePet ? value.pet?.weight : value.pets[petIndex].weight)
                const size = getSizeFromWeight(weight)
                form.setValue(singlePet ? 'pet.size' : `pets.${petIndex}.size`, size)
            }
        })
        return () => subscription.unsubscribe()
    }, [form, petIndex, getSizeFromWeight, singlePet])

    const petName = form.watch(singlePet ? 'pet.name' : `pets.${petIndex}.name`)
    const size = form.watch(singlePet ? 'pet.size' : `pets.${petIndex}.size`) as PetSize | undefined

    return (
        <Card className='mb-6 bg-gradient-to-br from-slate-50 to-white shadow-sm transition-shadow duration-200 hover:shadow-md'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
                <h3 className='text-lg font-semibold text-slate-800'>
                    {t('booking.step1.petFields.title', 'Mascota {{number}}', {
                        number: singlePet ? '' : petIndex + 1,
                    })}
                    {petName ? <span className='ml-1 text-primary'>- {petName}</span> : ''}
                </h3>
                {!singlePet && canRemove && (
                    <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        onClick={onRemove}
                        className='text-red-500 hover:bg-red-50 hover:text-red-700'
                    >
                        {t('booking.step1.removePet', 'Eliminar')}
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.name' : `pets.${petIndex}.name`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.name', 'Nombre')}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder={t('booking.step1.petFields.name', 'Nombre')}
                                        {...field}
                                        className='bg-white focus:ring-2'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.breed' : `pets.${petIndex}.breed`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.breed', 'Raza')}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder={t('booking.step1.petFields.breed', 'Raza')}
                                        {...field}
                                        className='bg-white focus:ring-2'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.weight' : `pets.${petIndex}.weight`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.weight', 'Peso (kg)')}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        type='number'
                                        step='0.1'
                                        min='0'
                                        placeholder={t('booking.step1.petFields.weight', 'Peso (kg)')}
                                        {...field}
                                        onChange={e => field.onChange(e.target.valueAsNumber)}
                                        className='bg-white focus:ring-2'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.size' : `pets.${petIndex}.size`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.size', 'Tamaño')}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        readOnly
                                        value={
                                            field.value
                                                ? String(t(`booking.step1.petFields.sizes.${field.value}`, field.value))
                                                : ''
                                        }
                                        className={cn(
                                            'cursor-not-allowed bg-white',
                                            field.value === 'pequeño' && 'text-green-600',
                                            field.value === 'mediano' && 'text-blue-600',
                                            field.value === 'grande' && 'text-orange-600',
                                        )}
                                    />
                                </FormControl>
                                {!removePricing && (
                                    <p className='mt-1 text-sm text-muted-foreground'>
                                        {size &&
                                            t('booking.pricePerDay', '{{price}}€/día', { price: petPrice.toFixed(2) })}
                                    </p>
                                )}
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.age' : `pets.${petIndex}.age`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.age', 'Edad')}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t(
                                            'booking.step1.petFields.agePlaceholder',
                                            'Ej: 2 años, 6 meses, 18 meses',
                                        )}
                                        {...field}
                                        className='bg-white focus:ring-2'
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.sex' : `pets.${petIndex}.sex`}
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className='text-slate-700'>
                                    {t('booking.step1.petFields.sex', 'Sexo')}
                                </FormLabel>
                                <FormControl>
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger className='bg-white focus:ring-2'>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='M'>
                                                {t('booking.step1.petFields.male', 'Macho')}
                                            </SelectItem>
                                            <SelectItem value='F'>
                                                {t('booking.step1.petFields.female', 'Hembra')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={singlePet ? 'pet.isNeutered' : `pets.${petIndex}.isNeutered`}
                        render={({ field }) => {
                            const isYes = field.value === true
                            return (
                                <FormItem>
                                    <FormLabel className='text-slate-700'>
                                        {t('booking.step1.petFields.isNeutered', 'Castrado/Esterilizado')}
                                    </FormLabel>
                                    <FormControl>
                                        <Select
                                            value={isYes ? 'yes' : 'no'}
                                            onValueChange={(value: 'yes' | 'no') => field.onChange(value === 'yes')}
                                        >
                                            <SelectTrigger
                                                className={cn(
                                                    'border-2 bg-white',
                                                    isYes ? 'border-green-200' : 'border-amber-200',
                                                )}
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent
                                                onCloseAutoFocus={e => {
                                                    e.preventDefault()
                                                }}
                                            >
                                                <SelectItem value='yes'>
                                                    {t('booking.step1.petFields.yes', 'Sí')}
                                                </SelectItem>
                                                <SelectItem value='no'>
                                                    {t('booking.step1.petFields.no', 'No')}
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )
                        }}
                    />
                </div>
                <div className='mt-4 space-y-4'>
                    {(() => {
                        const personalityValue = form.watch(
                            singlePet ? 'pet.personality' : `pets.${petIndex}.personality`,
                        )
                        const personalityWithOtherDogs = form.watch(
                            singlePet ? 'pet.personalityWithOtherDogs' : `pets.${petIndex}.personalityWithOtherDogs`,
                        )
                        // Lógica para determinar qué mostrar:
                        // - Si hay personalidad guardada Y NO hay campos separados llenos → campo único (no parseable)
                        // - Si no hay personalidad O hay campos separados llenos → campos separados (usuario nuevo o parseado)
                        const hasSavedPersonality = !!(
                            personalityValue &&
                            personalityValue.trim().length > 0 &&
                            !personalityWithOtherDogs
                        )

                        if (hasSavedPersonality) {
                            return (
                                <FormField
                                    control={form.control}
                                    name={singlePet ? 'pet.personality' : `pets.${petIndex}.personality`}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-slate-700'>
                                                {t('booking.step1.petFields.personality', 'Personalidad y hábitos')}
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder={t(
                                                        'booking.step1.petFields.personalityPlaceholder',
                                                        'Que carácter tiene con otros perros?\nY con personas que no conoce?\nTiene miedo a tormentas, petardos u otras cosas?',
                                                    )}
                                                    className='min-h-[100px] resize-y bg-white focus:ring-2'
                                                    {...field}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )
                        }

                        return (
                            <>
                                <FormField
                                    control={form.control}
                                    name={
                                        singlePet
                                            ? 'pet.personalityWithOtherDogs'
                                            : `pets.${petIndex}.personalityWithOtherDogs`
                                    }
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-slate-700'>
                                                {t(
                                                    'booking.step1.petFields.personalityWithOtherDogs.label',
                                                    '¿Qué carácter tiene con otros perros?',
                                                )}
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder={t(
                                                        'booking.step1.petFields.personalityWithOtherDogs.placeholder',
                                                        'Describe cómo se comporta tu mascota con otros perros',
                                                    )}
                                                    className='min-h-[80px] resize-y bg-white focus:ring-2'
                                                    {...field}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={
                                        singlePet
                                            ? 'pet.personalityWithStrangers'
                                            : `pets.${petIndex}.personalityWithStrangers`
                                    }
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-slate-700'>
                                                {t(
                                                    'booking.step1.petFields.personalityWithStrangers.label',
                                                    '¿Y con personas que no conoce?',
                                                )}
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder={t(
                                                        'booking.step1.petFields.personalityWithStrangers.placeholder',
                                                        'Describe cómo se comporta tu mascota con personas desconocidas',
                                                    )}
                                                    className='min-h-[80px] resize-y bg-white focus:ring-2'
                                                    {...field}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={singlePet ? 'pet.personalityFears' : `pets.${petIndex}.personalityFears`}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-slate-700'>
                                                {t(
                                                    'booking.step1.petFields.personalityFears.label',
                                                    '¿Tiene miedo a tormentas, petardos u otras cosas?',
                                                )}
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder={t(
                                                        'booking.step1.petFields.personalityFears.placeholder',
                                                        'Describe los miedos o fobias de tu mascota',
                                                    )}
                                                    className='min-h-[80px] resize-y bg-white focus:ring-2'
                                                    {...field}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </>
                        )
                    })()}
                </div>
            </CardContent>
        </Card>
    )
}
